/* eslint-disable ember/no-get */
// Extending From
import { RESTAdapter } from '@warp-drive/legacy/adapter/rest';
// Ember Imports
import { get } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
// External Imports
import config from '@linc-technologies/linced-common/utils/config';

/**
 * Base provides a base REST adapter that can be extended from for API models
 * that require token based authentication to obtain resources.
 */
export default class Base extends RESTAdapter {
  // Properties
  host = config.APP.apiHost;

  namespace = config.APP.apiPath;

  @service
  logging;

  @service
  fetch;

  @service
  session;

  // Override ember data's fetch function with our own so we can centralize fetches.
  _fetchRequest(options) {
    let method = (options.method || 'GET').toUpperCase();
    if (method === 'GET' || method === 'HEAD') {
      options.data = undefined;
    }

    let isTrustedHost = this.fetch.isTrustedHost(options.url);
    let responsePromise = this.fetch.fetchWithAuthentication(
      options.url,
      method,
      options,
      isTrustedHost,
    );

    // Run our response handling as a side effect so we can instrument http
    // datastore errors and logout if needed. We can't interfere with the actual
    // response or error cause ember data will throw a wobbly.
    // .then is nice here instead of await cause we don't want it to run before
    // return.
    return responsePromise
      .then((response) => {
        // Fire side-effect in a detached chain so throws from handleResponse
        // (which throws for any non-ok status) never reach ember-data.
        // ember-data needs to receive the raw Response for error responses so it
        // can read the body and call normalizeErrorResponse.
        Promise.resolve(response.clone())
          .then((cloned) => this.fetch.handleResponse(cloned, isTrustedHost))
          .catch((err) =>
            this.logging.pushError(err, 'emberData:handleResponseSideEffect', {
              requestUri: options.url,
            }),
          );
        return response; // pass response through unchanged
      })
      .catch((error) => {
        this.logging.pushError(error, 'emberData:_fetchRequest', {
          requestUri: options.url,
        });

        throw error; // rethrow so ember-data still sees errors
      });
  }

  handleResponse(status, headers, body) {
    if (status === 200 && Object.getOwnPropertyNames(body).length === 0) {
      // We got a 200 but with no results (body is an empty object literal)
      let plural = this.modelNamePlural;
      if (!isEmpty(plural)) {
        let hash = {};
        hash[this.modelNamePlural] = [];
        return hash;
      }
    }
    return super.handleResponse(...arguments);
  }

  normalizeErrorResponse(status, headers, payload) {
    if (
      payload &&
      typeof payload === 'object' &&
      payload.error &&
      payload.message
    ) {
      return [
        {
          status: get(payload, 'code'),
          title: get(payload, 'error'),
          detail: get(payload, 'message'),
        },
      ];
    }
    return super.normalizeErrorResponse(...arguments);
  }
}
